/** ASHRAEClimateZoneResponseModel */
export interface ASHRAEClimateZoneResponseModel {
  /** Climate Zone */
  climate_zone: string;
  /** Description */
  description: string;
  /** Latitude */
  latitude: number;
  /** Longitude */
  longitude: number;
  geo_json: GeoJSONGeometry;
}

/** AreaRequestModel */
export interface AreaRequestModel {
  /**
   * Buffer Distance
   * The buffer distance arround the asset
   * @example 0.5
   */
  buffer_distance: number;
  /**
   * Time Horizon
   * Time horizon describing the
   * @example 2022
   */
  time_horizon: number;
  /**
   * Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century
   * @example "RCP_2P6"
   */
  climate_scenario: ClimateScenario;
  /**
   * Model Id
   * The id of the hazard model requested. Note that if left blank the preferred model will be used.
   * @example "FSF_Flood_v2"
   */
  model_id: string;
  /**
   * End Point
   * path to the endpoint employed
   */
  end_point: string;
  /**
   * Geom Geojson
   * Geojson of the asset geometry
   */
  geom_geojson: string;
}

/** AvailableHazardModels */
export interface AvailableHazardModels {
  hazard_type: HazardType;
  sub_hazard_type?: SubHazardType | null;
  /** Region */
  region: Record<string, string[]> | null;
  /** Country */
  country: Record<string, string[]> | null;
  /** State */
  state: Record<string, string[]> | null;
}

/** BufferRequest */
export interface BufferRequest {
  /**
   * Geometry
   * GeoJSON definition of an asset geometry
   * @example {"geometry":{"coordinates":[-80.1943207,25.7683775],"type":"Point"},"properties":{"name":"Example Point","description":"This is an example point feature"},"type":"Feature"}
   */
  geometry: Record<string, any>;
  /**
   * Buffer Distance
   * The distance in km that a buffer geometry will be created, offset from the asset geometry.
   * @example 5.5
   */
  buffer_distance: number;
}

/** CacheModel */
export interface CacheModel {
  /**
   * The frequency metric of the download urls
   * @default "RETURN_PERIOD"
   */
  frequency_metric?: FrequencyMetric | null;
  /**
   * Extents
   * A string containing the extents of the bounds of the image
   */
  extents?: string | null;
  /**
   * Processed
   * s3 key to image file containing the processed PNG image to serve in Iris.
   */
  processed?: string | null;
  /**
   * Legend
   * s3 key to image file containing the legend image to serve in Iris.
   */
  legend?: string | null;
  /**
   * Raw
   * s3 key to image file containing the RAW, unprocessed image data.
   */
  raw?: string | null;
}

/** ClimateResultsBase */
export interface ClimateResultsBase {
  /**
   * Intensity Measures
   * A dictionary containing the hazard query results wherein each entry is an intensity measure.
   * @example {"PEAK_GROUND_ACCELERATION":{"frequency_metric":"RETURN_PERIOD","values":[0.01,0.1,0.5]},"PGV":{"frequency_metric":"RETURN_PERIOD","values":[100.324,320.334,455.34]}}
   */
  intensity_measures: Record<string, HazardIntensityMeasureBase>;
  [key: string]: any;
}

/** ClimateScenario */
export enum ClimateScenario {
  RCP1P9 = "RCP_1P9",
  RCP2P6 = "RCP_2P6",
  RCP3P4 = "RCP_3P4",
  RCP4P5 = "RCP_4P5",
  RCP6P0 = "RCP_6P0",
  RCP7P0 = "RCP_7P0",
  RCP8P5 = "RCP_8P5",
  SSP11P9 = "SSP1_1P9",
  SSP12P6 = "SSP1_2P6",
  SSP24P5 = "SSP2_4P5",
  SSP37P0 = "SSP3_7P0",
  SSP58P5 = "SSP5_8P5",
  Current = "Current",
}

/** ClimateScenarioModel */
export interface ClimateScenarioModel {
  /** Return Periods */
  return_periods: number[] | null;
  /** Percentiles */
  percentiles: string[] | null;
}

/** CostItemModel */
export interface CostItemModel {
  /**
   * Min
   * @min 0
   * @max 1000000
   */
  min: number;
  /**
   * Price Per Unit
   * @min 0
   * @max 1000000
   */
  price_per_unit: number;
  /**
   * Max
   * @min 0
   * @max 1000000
   */
  max: number;
  /** Description */
  description: string;
  /** Units */
  units?: string | null;
  /** Currency */
  currency?: string | null;
}

/** CostModel */
export interface CostModel {
  point: CostItemModel;
  area: CostItemModel;
  /**
   * Expensable
   * @default false
   */
  expensable?: boolean;
}

/** CostResponseModel */
export interface CostResponseModel {
  /** Area */
  area: number;
  /** Cost */
  cost: number;
  /** Model Id */
  model_id: string;
  /** Area Units */
  area_units?: string | null;
  /** Currency */
  currency?: string | null;
  /** Description */
  description?: string | null;
}

/** Country */
export enum Country {
  NotSuported = "Not_Suported",
  US = "US",
  GB = "GB",
  UK = "UK",
}

/** FrequencyMetric */
export enum FrequencyMetric {
  RETURN_PERIOD = "RETURN_PERIOD",
  SCALAR = "SCALAR",
  ANNUAL_PROBABILITY = "ANNUAL_PROBABILITY",
}

/** GeoJSONGeometry */
export interface GeoJSONGeometry {
  /**
   * Geometry
   * GeoJSON definition of an asset geometry
   * @example {"geometry":{"coordinates":[-80.1943207,25.7683775],"type":"Point"},"properties":{"name":"Example Point","description":"This is an example point feature"},"type":"Feature"}
   */
  geometry: Record<string, any>;
}

/** GeographyBase */
export interface GeographyBase {
  region: Region | null;
  country: Country | null;
  state: State | null;
}

/** HTTPValidationError */
export interface HTTPValidationError {
  /** Detail */
  detail?: ValidationError[];
}

/** HazardColdAreaResponse */
export interface HazardColdAreaResponse {
  /** @default {"intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: ClimateResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2021,"climate_scenario":"Current","model_id":"ASHRAE_meteo_2021","end_point":"area/heat","geom_geojson":"{'type': 'FeatureCollection', 'name': 'TestCirc', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-116.96555102655945, 32.63887429505082], [-116.95802082859247, 32.642307179418104], [-116.95881296250563, 32.63406939449952], [-116.96555102655945, 32.63887429505082]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASHRAE CLIMATIC DESIGN CONDITIONS 2021","hazard_type":"HEAT","sub_hazard_type":"HEAT","supplier":"http://ashrae-meteo.info/v2.0/index.php","version":"2.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2021":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[5,10,20,50]}}}}},"hazard_information":{"description":"Values are from the http://ashrae-meteo.info website API. The workflow first finds the closest airport within a 25-km buffer zone. If not airports are available, then the closest weather station is used.","intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"im_description":"Dry bulb temperature (regular temperature)","unit":"degC"},"EXTREME_ANNUAL_WET_BULB":{"im_description":"Wet bulb temperature (combination of temperature and humidity)","unit":"degC"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardColdPointResponse */
export interface HazardColdPointResponse {
  /** @default {"intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: ClimateResultsBase;
  /** @default {"buffer_distance":100,"time_horizon":2023,"climate_scenario":"Current","model_id":"NEXGDDP_CMIP6_COLD_v1","end_point":"point/cold","latitude":33.860102,"longitude":-116.331046} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6)","hazard_type":"COLD","sub_hazard_type":"COLD","supplier":"Arup Climate Crusaders Team","version":"1.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2024":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2030":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2035":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2050":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2065":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2075":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2080":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2095":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2100":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}}}},"hazard_information":{"description":"The NEX-GDDP-CMIP6 dataset is comprised of global downscaled climate scenarios derived from the General Circulation Model (GCM) runs conducted under the Coupled Model Intercomparison Project Phase 6 (CMIP6) and across all four “Tier 1” greenhouse gas emissions scenarios known as Shared Socioeconomic Pathways (SSPs). 0.25 degrees (25 km) spatial resolution.","intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"im_description":"The minimum dry bulb temperature, or the ambient air temperature. Dry bulb temperature does not consider any form of moisture content in its calculation.","unit":"degC"},"EXTREME_ANNUAL_WET_BULB":{"im_description":"The minimum wet bulb temperature. Wet bulb temperature is read by a thermometer covered in cloth which has been soaked in water.","unit":"degC"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardDroughtAreaResponse */
export interface HazardDroughtAreaResponse {
  /** @default {"intensity_measures":{"WATER_DEPLETION":{"frequency_metric":"SCALAR","values":[0.23]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2024,"climate_scenario":"Current","model_id":"AQUEDUCT_DROUGHT_v4","end_point":"area/drought","geom_geojson":"{'type': 'FeatureCollection', 'name': 'sweden_triangle', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[17.658071283058163, 59.85551842217537], [17.66151942861287, 59.85676837493896], [17.662338363182116, 59.85280300755104], [17.658071283058163, 59.85551842217537]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"Aqueduct 4.0 Water Risk Atlas - Baseline Water Depletion (BWD)","hazard_type":"DROUGHT","sub_hazard_type":"DROUGHT","supplier":"World Resources Institute","version":"1.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2024":{"climate_scenarios":{"Current":{}}}}},"hazard_information":{"description":"Baseline Water Depletion (BWD) in Aqueduct 4.0 measures the ratio of total water consumption to available blue water resources. It accounts for domestic, industrial, irrigation, and livestock water usage against renewable water supplies, factoring in upstream consumption and dam impacts. Higher values indicate greater stress on local water supplies and reduced availability for downstream users. The data is presented with raw values (_raw), 0-5 scores (_score), descriptive labels (_label), and categorical integers (_cat) that can be used for visualization purposes.","intensity_measures":{"WATER_DEPLETION":{"im_description":"Baseline water depletion measures the ratio of total water consumption to available blue water.","unit":"dimensionless"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardDroughtPointResponse */
export interface HazardDroughtPointResponse {
  /** @default {"intensity_measures":{"WATER_DEPLETION":{"frequency_metric":"SCALAR","values":[0.23]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2024,"climate_scenario":"Current","model_id":"AQUEDUCT_DROUGHT_v4","end_point":"point/drought","latitude":37.87397,"longitude":-122.2486} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"Aqueduct 4.0 Water Risk Atlas - Baseline Water Depletion (BWD)","hazard_type":"DROUGHT","sub_hazard_type":"DROUGHT","supplier":"World Resources Institute","version":"1.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2024":{"climate_scenarios":{"Current":{}}}}},"hazard_information":{"description":"Baseline Water Depletion (BWD) in Aqueduct 4.0 measures the ratio of total water consumption to available blue water resources. It accounts for domestic, industrial, irrigation, and livestock water usage against renewable water supplies, factoring in upstream consumption and dam impacts. Higher values indicate greater stress on local water supplies and reduced availability for downstream users. The data is presented with raw values (_raw), 0-5 scores (_score), descriptive labels (_label), and categorical integers (_cat) that can be used for visualization purposes.","intensity_measures":{"WATER_DEPLETION":{"im_description":"Baseline water depletion measures the ratio of total water consumption to available blue water.","unit":"dimensionless"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardFireAreaResponse */
export interface HazardFireAreaResponse {
  /** @default {"intensity_measures":{"FLAME_LENGTH":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":0.5,"time_horizon":2022,"climate_scenario":"Current","model_id":"FSF_Wildfire_v2","end_point":"area/fire","geom_geojson":"{'type': 'FeatureCollection', 'name': 'TestCirc', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-116.96555102655945, 32.63887429505082], [-116.95802082859247, 32.642307179418104], [-116.95881296250563, 32.63406939449952], [-116.96555102655945, 32.63887429505082]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"FSF Wildfire v2","hazard_type":"WILDFIRE","sub_hazard_type":"WILDFIRE","supplier":"First Street Foundation","version":"2.0","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2023":{"climate_scenarios":{"Current":{"return_periods":[50,100,500,1000,5000,10000,50000,100000]}}},"2053":{"climate_scenarios":{"SSP2_4P5":{}}}}},"hazard_information":{"description":"Values provided by the First Street Foundation v2.0 wildfire model raster datasets hosted at Arup. The spatial resolution of the rasters is 30 m.","intensity_measures":{"ADJACENT_FLAME_LENGTH":{"im_description":"Flame length (ft) modeled at the most hazardous pixel adjacent to the parcel pixel. The most hazardous adjacent pixel is selected as the pixel with the maximum burn probability.","unit":"ft"},"BURN":{"im_description":"Burning or any flame length greater than 0.","unit":"dimensionless"},"EMBER_COUNTS":{"im_description":"Ember count modelled at the pixel containing the asset. The ember counts are unitless values from the wildfire hazard model.","unit":"count"},"FLAME_LENGTH":{"im_description":"Flame length (ft) modeled at the pixel containing the asset.","unit":"ft"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardFirePointResponse */
export interface HazardFirePointResponse {
  /** @default {"intensity_measures":{"FLAME_LENGTH":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":0.5,"time_horizon":2022,"climate_scenario":"Current","model_id":"FSF_Wildfire_v2","end_point":"point/fire","latitude":38.705506,"longitude":-122.598881} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"FSF Wildfire v2","hazard_type":"WILDFIRE","sub_hazard_type":"WILDFIRE","supplier":"First Street Foundation","version":"2.0","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2023":{"climate_scenarios":{"Current":{"return_periods":[50,100,500,1000,5000,10000,50000,100000]}}},"2053":{"climate_scenarios":{"SSP2_4P5":{}}}}},"hazard_information":{"description":"Values provided by the First Street Foundation v2.0 wildfire model raster datasets hosted at Arup. The spatial resolution of the rasters is 30 m.","intensity_measures":{"ADJACENT_FLAME_LENGTH":{"im_description":"Flame length (ft) modeled at the most hazardous pixel adjacent to the parcel pixel. The most hazardous adjacent pixel is selected as the pixel with the maximum burn probability.","unit":"ft"},"BURN":{"im_description":"Burning or any flame length greater than 0.","unit":"dimensionless"},"EMBER_COUNTS":{"im_description":"Ember count modelled at the pixel containing the asset. The ember counts are unitless values from the wildfire hazard model.","unit":"count"},"FLAME_LENGTH":{"im_description":"Flame length (ft) modeled at the pixel containing the asset.","unit":"ft"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardFloodAreaResponse */
export interface HazardFloodAreaResponse {
  /** @default {"intensity_measures":{"FLOOD_DEPTH":{"all_stats":[{"count":33519,"max":311,"mean":103.5436618037531,"median":89,"min":5,"range":306,"std":54.3806495449278}],"frequency_metric":"RETURN_PERIOD","return_periods":[500],"source":["coastal"],"values":[311]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":0.5,"time_horizon":2052,"climate_scenario":"RCP_4P5","model_id":"FSF_Flood_v2","end_point":"area/flood","geom_geojson":"{'type': 'FeatureCollection', 'name': 'Square', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-80.08117896211209, 26.736416497181196], [-80.08117896211209, 26.736656538979233], [-80.0808378500833, 26.736656538979233], [-80.0808378500833, 26.736416497181196], [-80.08117896211209, 26.736416497181196]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"FSF Integrated Flood v2.0","hazard_type":"FLOOD","sub_hazard_type":"INTEGRATED_FLOODING","supplier":"First Street Foundation","version":"2.0","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"percentiles":["mean"],"return_periods":[5,20,100,500]}}},"2037":{"climate_scenarios":{"RCP_4P5":{"percentiles":["mean"],"return_periods":[5,20,100,500]}}},"2052":{"climate_scenarios":{"RCP_4P5":{"percentiles":["mean"],"return_periods":[5,20,100,500]}}}}},"hazard_information":{"description":"Values provided by the First Street Foundation v2.0 integrated flood model raster datasets hosted at Arup. The spatial resolution of the rasters is 3 m.","intensity_measures":{"FLOOD_DEPTH":{"im_description":"Maximum flood depth relative to the surrounding ground.","unit":"cm"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardFloodPointResponse */
export interface HazardFloodPointResponse {
  /** @default {"intensity_measures":{"FLOOD_DEPTH":{"frequency_metric":"RETURN_PERIOD","return_periods":[20],"source":["coastal"],"values":[223]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":0.5,"time_horizon":2052,"climate_scenario":"RCP_4P5","model_id":"FSF_Flood_v2","end_point":"point/flood","latitude":27.52618,"longitude":-80.34837} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"FSF Integrated Flood v2.0","hazard_type":"FLOOD","sub_hazard_type":"INTEGRATED_FLOODING","supplier":"First Street Foundation","version":"2.0","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"percentiles":["mean"],"return_periods":[5,20,100,500]}}},"2037":{"climate_scenarios":{"RCP_4P5":{"percentiles":["mean"],"return_periods":[5,20,100,500]}}},"2052":{"climate_scenarios":{"RCP_4P5":{"percentiles":["mean"],"return_periods":[5,20,100,500]}}}}},"hazard_information":{"description":"Values provided by the First Street Foundation v2.0 integrated flood model raster datasets hosted at Arup. The spatial resolution of the rasters is 3 m.","intensity_measures":{"FLOOD_DEPTH":{"im_description":"Maximum flood depth relative to the surrounding ground.","unit":"cm"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardHeatAreaResponse */
export interface HazardHeatAreaResponse {
  /** @default {"intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: ClimateResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2021,"climate_scenario":"Current","model_id":"ASHRAE_meteo_2021","end_point":"area/heat","geom_geojson":"{'type': 'FeatureCollection', 'name': 'TestCirc', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-116.96555102655945, 32.63887429505082], [-116.95802082859247, 32.642307179418104], [-116.95881296250563, 32.63406939449952], [-116.96555102655945, 32.63887429505082]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASHRAE CLIMATIC DESIGN CONDITIONS 2021","hazard_type":"HEAT","sub_hazard_type":"HEAT","supplier":"http://ashrae-meteo.info/v2.0/index.php","version":"2.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2021":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[5,10,20,50]}}}}},"hazard_information":{"description":"Values are from the http://ashrae-meteo.info website API. The workflow first finds the closest airport within a 25-km buffer zone. If not airports are available, then the closest weather station is used.","intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"im_description":"Dry bulb temperature (regular temperature)","unit":"degC"},"EXTREME_ANNUAL_WET_BULB":{"im_description":"Wet bulb temperature (combination of temperature and humidity)","unit":"degC"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardHeatPointResponse */
export interface HazardHeatPointResponse {
  /** @default {"intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: ClimateResultsBase;
  /** @default {"buffer_distance":100,"time_horizon":2022,"climate_scenario":"Current","model_id":"NEXGDDP_CMIP6_HEAT_v1","end_point":"point/heat","latitude":33.860102,"longitude":-116.331046} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6)","hazard_type":"HEAT","sub_hazard_type":"HEAT","supplier":"Arup Climate Crusaders Team","version":"1.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2024":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2030":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2035":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2050":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2065":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2075":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2080":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2095":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2100":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}}}},"hazard_information":{"description":"The NEX-GDDP-CMIP6 dataset is comprised of global downscaled climate scenarios derived from the General Circulation Model (GCM) runs conducted under the Coupled Model Intercomparison Project Phase 6 (CMIP6) and across all four “Tier 1” greenhouse gas emissions scenarios known as Shared Socioeconomic Pathways (SSPs). 0.25 degrees (25 km) spatial resolution.","intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"im_description":"The maximum dry bulb temperature, or the ambient air temperature. Dry bulb temperature does not consider any form of moisture content in its calculation.","unit":"degC"},"EXTREME_ANNUAL_WET_BULB":{"im_description":"The maximum wet bulb temperature. Wet bulb temperature is read by a thermometer covered in cloth which has been soaked in water.","unit":"degC"},"HEAT_INDEX":{"im_description":"The maximum heat index, or the apparent air temperature. Heat index is a measure of how hot it feels to the human body when considering the joint, coincident effects of relative humidity and dry bulb temperature.","unit":"degC"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardIceAreaResponse */
export interface HazardIceAreaResponse {
  /** @default {"intensity_measures":{"ICE_THICKNESS":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":300,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Ice","end_point":"area/ice","geom_geojson":"{'type': 'FeatureCollection', 'name': 'Square', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-80.08117896211209, 26.736416497181196], [-80.08117896211209, 26.736656538979233], [-80.0808378500833, 26.736656538979233], [-80.0808378500833, 26.736416497181196], [-80.08117896211209, 26.736416497181196]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 Ice load standard","hazard_type":"ICE","sub_hazard_type":"ICE_THICKNESS","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[250,500,1000,1400]}}}}},"hazard_information":{"description":"Json dictionary containing the radial ice thicknesses due to freezing rain for 250, 500, 1,000, and 1,400-year mean recurrence intervals along with concurrent 3-s gust speeds (mph) and concurrent air temperatures (F) from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722. Values provided are ice thickness (in). Data Source: Standard ASCE/SEI 7-22, Figs. 10-2 through 10-8.","intensity_measures":{"ICE_THICKNESS":{"im_description":"Ice thickness","unit":"in"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardIcePointResponse */
export interface HazardIcePointResponse {
  /** @default {"intensity_measures":{"ICE_THICKNESS":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Ice","end_point":"point/ice","latitude":37.205909,"longitude":-93.309871} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 Ice load standard","hazard_type":"ICE","sub_hazard_type":"ICE_THICKNESS","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[250,500,1000,1400]}}}}},"hazard_information":{"description":"Json dictionary containing the radial ice thicknesses due to freezing rain for 250, 500, 1,000, and 1,400-year mean recurrence intervals along with concurrent 3-s gust speeds (mph) and concurrent air temperatures (F) from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722. Values provided are ice thickness (in). Data Source: Standard ASCE/SEI 7-22, Figs. 10-2 through 10-8.","intensity_measures":{"ICE_THICKNESS":{"im_description":"Ice thickness","unit":"in"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardInfoModel */
export interface HazardInfoModel {
  /**
   * Description
   * Text description of the hazard model
   * @example "Json dictionary containing the site-specific seismic hazard curves from the 2018 USGS National Seismic Hazard Model (NSHM) - https://earthquake.usgs.gov/nshmp/"
   */
  description: string;
  /**
   * Intensity Measures
   * Dictionary containing the intensity measures that the hazard model returns
   * @example "Json dictionary containing the site-specific seismic hazard curves from the 2018 USGS National Seismic Hazard Model (NSHM) - https://earthquake.usgs.gov/nshmp/"
   */
  intensity_measures: Record<string, IntensityMeasureModel>;
}

/** HazardIntensityMeasureBase */
export interface HazardIntensityMeasureBase {
  /**
   * Values
   * A list containing the values of the intensity measures.
   * @example [10.324,32.334,2355.34]
   */
  values?: any[] | null;
  /**
   * The frequency metric of the intensity measure
   * @default "RETURN_PERIOD"
   */
  frequency_metric?: FrequencyMetric | null;
  /** Download Urls */
  download_urls?: Record<string, CacheModel | null> | null;
  /**
   * Percentiles
   * A list containing the supported percentiles (if any).
   * @example [1,50,99,99.6]
   */
  percentiles?: number[] | null;
  /**
   * Values Percentiles
   * A list containing the values of the supported percentiles (if any).
   * @example [14.3,34.61,35.19]
   */
  values_percentiles?: any[] | null;
  /**
   * Annual Probabilities Thresholds
   * A list containing the thresholds of annual probabilities.
   * @example [1,4,99,99.6]
   */
  annual_probabilities_thresholds?: number[] | null;
  /**
   * Annual Probabilities
   * A list containing the annual probabilities associated with exceeding the intensity measure values (optional).
   * @example [0.67,0.13,0.05]
   */
  annual_probabilities?: any[] | null;
  [key: string]: any;
}

/** HazardPrecipitationAreaResponse */
export interface HazardPrecipitationAreaResponse {
  /** @default {"intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: ClimateResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2021,"climate_scenario":"Current","model_id":"ASHRAE_meteo_2021","end_point":"area/heat","geom_geojson":"{'type': 'FeatureCollection', 'name': 'TestCirc', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-116.96555102655945, 32.63887429505082], [-116.95802082859247, 32.642307179418104], [-116.95881296250563, 32.63406939449952], [-116.96555102655945, 32.63887429505082]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASHRAE CLIMATIC DESIGN CONDITIONS 2021","hazard_type":"HEAT","sub_hazard_type":"HEAT","supplier":"http://ashrae-meteo.info/v2.0/index.php","version":"2.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2021":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[5,10,20,50]}}}}},"hazard_information":{"description":"Values are from the http://ashrae-meteo.info website API. The workflow first finds the closest airport within a 25-km buffer zone. If not airports are available, then the closest weather station is used.","intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"im_description":"Dry bulb temperature (regular temperature)","unit":"degC"},"EXTREME_ANNUAL_WET_BULB":{"im_description":"Wet bulb temperature (combination of temperature and humidity)","unit":"degC"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardPrecipitationPointResponse */
export interface HazardPrecipitationPointResponse {
  /** @default {"intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: ClimateResultsBase;
  /** @default {"buffer_distance":100,"time_horizon":2022,"climate_scenario":"Current","model_id":"NEXGDDP_CMIP6_HEAT_v1","end_point":"point/heat","latitude":33.860102,"longitude":-116.331046} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6)","hazard_type":"HEAT","sub_hazard_type":"HEAT","supplier":"Arup Climate Crusaders Team","version":"1.0","supported_geography":{"region":"Global"},"temporal_meta":{"time_horizons":{"2024":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2030":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2035":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2050":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2065":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2075":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2080":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2095":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}},"2100":{"climate_scenarios":{"SSP2_4P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP3_7P0":{"percentiles":["50"],"return_periods":[1,5,10,20,50]},"SSP5_8P5":{"percentiles":["50"],"return_periods":[1,5,10,20,50]}}}}},"hazard_information":{"description":"The NEX-GDDP-CMIP6 dataset is comprised of global downscaled climate scenarios derived from the General Circulation Model (GCM) runs conducted under the Coupled Model Intercomparison Project Phase 6 (CMIP6) and across all four “Tier 1” greenhouse gas emissions scenarios known as Shared Socioeconomic Pathways (SSPs). 0.25 degrees (25 km) spatial resolution.","intensity_measures":{"EXTREME_ANNUAL_DRY_BULB":{"im_description":"The maximum dry bulb temperature, or the ambient air temperature. Dry bulb temperature does not consider any form of moisture content in its calculation.","unit":"degC"},"EXTREME_ANNUAL_WET_BULB":{"im_description":"The maximum wet bulb temperature. Wet bulb temperature is read by a thermometer covered in cloth which has been soaked in water.","unit":"degC"},"HEAT_INDEX":{"im_description":"The maximum heat index, or the apparent air temperature. Heat index is a measure of how hot it feels to the human body when considering the joint, coincident effects of relative humidity and dry bulb temperature.","unit":"degC"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardResultsBase */
export interface HazardResultsBase {
  /**
   * Intensity Measures
   * A dictionary containing the hazard query results wherein each entry is an intensity measure.
   * @example {"PEAK_GROUND_ACCELERATION":{"frequency_metric":"RETURN_PERIOD","values":[0.01,0.1,0.5]},"PGV":{"frequency_metric":"RETURN_PERIOD","values":[100.324,320.334,455.34]}}
   */
  intensity_measures: Record<string, HazardIntensityMeasureBase>;
  [key: string]: any;
}

/** HazardSeismicAreaResponse */
export interface HazardSeismicAreaResponse {
  /** @default {"intensity_measures":{"PEAK_GROUND_ACCELERATION":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2018,"climate_scenario":"Current","model_id":"USGS_NSHM_2018","end_point":"area/seismic","geom_geojson":"{'type': 'FeatureCollection', 'name': 'Fremont', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-122.0526602582704, 37.561705867971924], [-122.0526602582704, 37.56242224686365], [-122.05147920117862, 37.56242224686365], [-122.05147920117862, 37.561705867971924], [-122.0526602582704, 37.561705867971924]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"2018 USGS National Seismic Hazard Model (NSHM) - Dynamic API","hazard_type":"SEISMIC","sub_hazard_type":"EARTHQUAKE_SHAKING","supplier":"United States Geological Service","version":"1.0","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2018":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[5,10,43,50,72,100,225,475,500,975,1000,2475,2500,5000]}}}}},"hazard_information":{"description":"Json dictionary containing the site-specific seismic hazard curves from the 2018 USGS National Seismic Hazard Model (NSHM) - https://earthquake.usgs.gov/nshmp/","intensity_measures":{"PEAK_GROUND_ACCELERATION":{"im_description":"Peak ground acceleration","unit":"g_n"},"PEAK_GROUND_VELOCITY":{"im_description":"Peak ground velocity","unit":"cm/s"},"SPECTRAL_ACCELERATION_T0P01":{"im_description":"Spectral acceleration at the following periods [s]: 0.01","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P02":{"im_description":"Spectral acceleration at the following periods [s]: 0.02","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P03":{"im_description":"Spectral acceleration at the following periods [s]: 0.03","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P05":{"im_description":"Spectral acceleration at the following periods [s]: 0.05","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P075":{"im_description":"Spectral acceleration at the following periods [s]: 0.075","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P1":{"im_description":"Spectral acceleration at the following periods [s]: 0.1","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P15":{"im_description":"Spectral acceleration at the following periods [s]: 0.15","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P2":{"im_description":"Spectral acceleration at the following periods [s]: 0.2","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P25":{"im_description":"Spectral acceleration at the following periods [s]: 0.25","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P3":{"im_description":"Spectral acceleration at the following periods [s]:  0.3","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P4":{"im_description":"Spectral acceleration at the following periods [s]:  0.4","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P5":{"im_description":"Spectral acceleration at the following periods [s]:  0.5","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P75":{"im_description":"Spectral acceleration at the following periods [s]:  0.75","unit":"g_n"},"SPECTRAL_ACCELERATION_T10":{"im_description":"Spectral acceleration at the following periods [s]:10.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T1P0":{"im_description":"Spectral acceleration at the following periods [s]:  1.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T1P5":{"im_description":"Spectral acceleration at the following periods [s]:  1.5","unit":"g_n"},"SPECTRAL_ACCELERATION_T2P0":{"im_description":"Spectral acceleration at the following periods [s]:  2.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T3P0":{"im_description":"Spectral acceleration at the following periods [s]:  3.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T4P0":{"im_description":"Spectral acceleration at the following periods [s]: 4.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T5P0":{"im_description":"Spectral acceleration at the following periods [s]: 5.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T7P5":{"im_description":"Spectral acceleration at the following periods [s]: 7.5","unit":"g_n"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardSeismicPointResponse */
export interface HazardSeismicPointResponse {
  /** @default {"intensity_measures":{"PEAK_GROUND_ACCELERATION":{"AnnualFrequencyExceedance":[0.80660604,0.62982802,0.47936476,0.35633541],"ReturnPeriod":[1,2,2,3],"frequency_metric":"RETURN_PERIOD","values":[0.00233,0.0035,0.00524,0.00786]},"PEAK_GROUND_VELOCITY":{"AnnualFrequencyExceedance":[0.75969511,0.57796615,0.42590651,0.30544536],"ReturnPeriod":[1,2,2,3],"frequency_metric":"RETURN_PERIOD","values":[0.237,0.355,0.532,0.798]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2018,"climate_scenario":"Current","model_id":"USGS_NSHM_2018","end_point":"point/seismic","latitude":37.87397,"longitude":-122.2486} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"2018 USGS National Seismic Hazard Model (NSHM) - Dynamic API","hazard_type":"SEISMIC","sub_hazard_type":"EARTHQUAKE_SHAKING","supplier":"United States Geological Service","version":"1.0","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2018":{"climate_scenarios":{"Current":{"percentiles":["50"],"return_periods":[5,10,43,50,72,100,225,475,500,975,1000,2475,2500,5000]}}}}},"hazard_information":{"description":"Json dictionary containing the site-specific seismic hazard curves from the 2018 USGS National Seismic Hazard Model (NSHM) - https://earthquake.usgs.gov/nshmp/","intensity_measures":{"PEAK_GROUND_ACCELERATION":{"im_description":"Peak ground acceleration","unit":"g_n"},"PEAK_GROUND_VELOCITY":{"im_description":"Peak ground velocity","unit":"cm/s"},"SPECTRAL_ACCELERATION_T0P01":{"im_description":"Spectral acceleration at the following periods [s]: 0.01","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P02":{"im_description":"Spectral acceleration at the following periods [s]: 0.02","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P03":{"im_description":"Spectral acceleration at the following periods [s]: 0.03","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P05":{"im_description":"Spectral acceleration at the following periods [s]: 0.05","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P075":{"im_description":"Spectral acceleration at the following periods [s]: 0.075","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P1":{"im_description":"Spectral acceleration at the following periods [s]: 0.1","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P15":{"im_description":"Spectral acceleration at the following periods [s]: 0.15","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P2":{"im_description":"Spectral acceleration at the following periods [s]: 0.2","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P25":{"im_description":"Spectral acceleration at the following periods [s]: 0.25","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P3":{"im_description":"Spectral acceleration at the following periods [s]:  0.3","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P4":{"im_description":"Spectral acceleration at the following periods [s]:  0.4","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P5":{"im_description":"Spectral acceleration at the following periods [s]:  0.5","unit":"g_n"},"SPECTRAL_ACCELERATION_T0P75":{"im_description":"Spectral acceleration at the following periods [s]:  0.75","unit":"g_n"},"SPECTRAL_ACCELERATION_T10":{"im_description":"Spectral acceleration at the following periods [s]:10.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T1P0":{"im_description":"Spectral acceleration at the following periods [s]:  1.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T1P5":{"im_description":"Spectral acceleration at the following periods [s]:  1.5","unit":"g_n"},"SPECTRAL_ACCELERATION_T2P0":{"im_description":"Spectral acceleration at the following periods [s]:  2.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T3P0":{"im_description":"Spectral acceleration at the following periods [s]:  3.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T4P0":{"im_description":"Spectral acceleration at the following periods [s]: 4.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T5P0":{"im_description":"Spectral acceleration at the following periods [s]: 5.0","unit":"g_n"},"SPECTRAL_ACCELERATION_T7P5":{"im_description":"Spectral acceleration at the following periods [s]: 7.5","unit":"g_n"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardSnowAreaResponse */
export interface HazardSnowAreaResponse {
  /** @default {"intensity_measures":{"GROUND_SNOW_LOAD":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Snow","end_point":"area/snow","geom_geojson":"{'type': 'FeatureCollection', 'name': 'Square', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-80.08117896211209, 26.736416497181196], [-80.08117896211209, 26.736656538979233], [-80.0808378500833, 26.736656538979233], [-80.0808378500833, 26.736416497181196], [-80.08117896211209, 26.736416497181196]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 snow load standard","hazard_type":"SNOW","sub_hazard_type":"SNOW_PRESSURE","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[20]}}}}},"hazard_information":{"description":"Json dictionary containing the ground snow load (Pg) at the 20 year mean-recurrence interval (MRIs). The values come from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722. Values provided are ground snow loads. In areas designated 'case study required,' extreme local variations in ground snow loads preclude mapping at this scale. Numbers in parentheses represent the upper elevation limits in feet for the ground snow load values presented. Site-specific case studies are required to establish ground snow loads at elevations not covered. Snow load values are mapped to a 0.5 mile resolution. This resolution can create a mismatch between the mapped elevation and the site-specific elevation in topographically complex areas. Engineers should consult the local authority having jurisdiction in locations where the reported ‘elevation’ and ‘mapped elevation’ differ significantly from each other. Data Source: ASCE/SEI 7-22, Figures 7.6-1 and 7.6-2 A-D","intensity_measures":{"GROUND_SNOW_LOAD":{"im_description":"Ground snow load (Pg)","unit":"lb / ft ** 2"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardSnowPointResponse */
export interface HazardSnowPointResponse {
  /** @default {"intensity_measures":{"GROUND_SNOW_LOAD":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Snow","end_point":"point/snow","latitude":39.47179,"longitude":-118.77481} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 snow load standard","hazard_type":"SNOW","sub_hazard_type":"SNOW_PRESSURE","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[20]}}}}},"hazard_information":{"description":"Json dictionary containing the ground snow load (Pg) at the 20 year mean-recurrence interval (MRIs). The values come from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722. Values provided are ground snow loads. In areas designated 'case study required,' extreme local variations in ground snow loads preclude mapping at this scale. Numbers in parentheses represent the upper elevation limits in feet for the ground snow load values presented. Site-specific case studies are required to establish ground snow loads at elevations not covered. Snow load values are mapped to a 0.5 mile resolution. This resolution can create a mismatch between the mapped elevation and the site-specific elevation in topographically complex areas. Engineers should consult the local authority having jurisdiction in locations where the reported ‘elevation’ and ‘mapped elevation’ differ significantly from each other. Data Source: ASCE/SEI 7-22, Figures 7.6-1 and 7.6-2 A-D","intensity_measures":{"GROUND_SNOW_LOAD":{"im_description":"Ground snow load (Pg)","unit":"lb / ft ** 2"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardSourceModel */
export interface HazardSourceModel {
  /** Model Name */
  model_name: string;
  hazard_type: HazardType;
  sub_hazard_type?: SubHazardType | null;
  /** Supplier */
  supplier: string;
  /** Version */
  version: string;
  supported_geography: GeographyBase;
  temporal_meta: TemporalMetaModel;
  hazard_information: HazardInfoModel;
  cost: CostModel;
  /** Additional Args */
  additional_args?: Record<string, any> | null;
  /** Extent Envelope Geometry */
  extent_envelope_geometry?: null;
}

/** HazardTornadoAreaResponse */
export interface HazardTornadoAreaResponse {
  /** @default {"intensity_measures":{"WIND_SPEED":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":300,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Tornado","end_point":"area/tornado","geom_geojson":"{'type': 'FeatureCollection', 'name': 'Square', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-80.08117896211209, 26.736416497181196], [-80.08117896211209, 26.736656538979233], [-80.0808378500833, 26.736656538979233], [-80.0808378500833, 26.736416497181196], [-80.08117896211209, 26.736416497181196]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 Tornado","hazard_type":"TORNADO","sub_hazard_type":"TORNADO","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[300,700,1000,1700,3000,10000,100000,1000000,10000000]}}}}},"hazard_information":{"description":"Json dictionary containing tornado speeds from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722 Tornado Geodatabase. Values provided are tornado wind speed (mph) calculated from linear interpolation using the logarithm of the effective plan areas, Ae, (square feet) from the ASCE 7-22 response. To select the appropriate tornado hazard map, the effective plan area, Ae, is determined from the supplied geojson if the area endpoint is employed. If the lat./lon. value endpoint is employed, a default effective plan area of 2000 square feet is used. Alternatively, the effective plan area can be passed as an additional argument (additional_args) to HAPI, e.g., Ae=5000. Linear interpolation of tornado speed between maps uses the logarithm of the effective plan area size per Section 32.5.1. Data Source: ASCE/SEI Standard 7-22, Figs. 32.5-1, 32.5-2, and G.2-1 through -4","intensity_measures":{"WIND_SPEED":{"im_description":"Tornado Wind Speed from linear interpolation of tornado speed maps in ASCE 7-22 response using the logarithm of the effective plan area","unit":"mph"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardTornadoPointResponse */
export interface HazardTornadoPointResponse {
  /** @default {"intensity_measures":{"WIND_SPEED":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Tornado","end_point":"point/tornado","latitude":42.234109,"longitude":-106.921695} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 Tornado","hazard_type":"TORNADO","sub_hazard_type":"TORNADO","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[300,700,1000,1700,3000,10000,100000,1000000,10000000]}}}}},"hazard_information":{"description":"Json dictionary containing tornado speeds from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722 Tornado Geodatabase. Values provided are tornado wind speed (mph) calculated from linear interpolation using the logarithm of the effective plan areas, Ae, (square feet) from the ASCE 7-22 response. To select the appropriate tornado hazard map, the effective plan area, Ae, is determined from the supplied geojson if the area endpoint is employed. If the lat./lon. value endpoint is employed, a default effective plan area of 2000 square feet is used. Alternatively, the effective plan area can be passed as an additional argument (additional_args) to HAPI, e.g., Ae=5000. Linear interpolation of tornado speed between maps uses the logarithm of the effective plan area size per Section 32.5.1. Data Source: ASCE/SEI Standard 7-22, Figs. 32.5-1, 32.5-2, and G.2-1 through -4","intensity_measures":{"WIND_SPEED":{"im_description":"Tornado Wind Speed from linear interpolation of tornado speed maps in ASCE 7-22 response using the logarithm of the effective plan area","unit":"mph"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardType */
export enum HazardType {
  FLOOD = "FLOOD",
  WILDFIRE = "WILDFIRE",
  SEISMIC = "SEISMIC",
  WIND = "WIND",
  HEAT = "HEAT",
  COLD = "COLD",
  PRECIPITATION = "PRECIPITATION",
  SNOW = "SNOW",
  ICE = "ICE",
  TORNADO = "TORNADO",
  DROUGHT = "DROUGHT",
}

/** HazardWindAreaResponse */
export interface HazardWindAreaResponse {
  /** @default {"intensity_measures":{"MAX_3S_GUST":{"frequency_metric":"RETURN_PERIOD","values":[1,2,3]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Wind","end_point":"area/wind","geom_geojson":"{'type': 'FeatureCollection', 'name': 'Houston', 'crs': {'type': 'name', 'properties': {'name': 'urn:ogc:def:crs:OGC:1.3:CRS84'}}, 'features': [{'type': 'Feature', 'properties': {}, 'geometry': {'type': 'Polygon', 'coordinates': [[[-94.81883765143968, 29.275907183562733], [-94.81669234067604, 29.27582470745197], [-94.81795448094736, 29.27467407759232], [-94.81718358385515, 29.27399136294864], [-94.81817110767189, 29.27394087944775], [-94.81905427816422, 29.273857561476778], [-94.81953752239586, 29.274574096027152], [-94.82035902968269, 29.27484222109373], [-94.8187876606571, 29.276473745765355], [-94.81883765143968, 29.275907183562733]]]}}]}"} */
  request?: AreaRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 wind load standard","hazard_type":"WIND","sub_hazard_type":"INTEGRATED_WIND","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[10,25,50,100,300,700,1700,3000,10000]}}}}},"hazard_information":{"description":"Json dictionary containing the windspeeds across a range of mean-recurrence intervals (MRIs). Specifically, the 10, 25, 50, 100, 300, 700, 1700, 3000, 10000, 100000, and 1000000 year MRIs. The values come from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722","intensity_measures":{"MAX_3S_GUST":{"im_description":"3-second sustained gust windspeed","unit":"mph"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** HazardWindPointResponse */
export interface HazardWindPointResponse {
  /** @default {"intensity_measures":{"MAX_3S_GUST":{"elevation":67.81,"frequency_metric":"RETURN_PERIOD","return_periods":["10","25","50","100","300","700","1700","3000","10000","100000","1000000"],"values":[75.999,86.1577,94.9158,105.236,116.558,126.97,134.314,138.416,148.508,164.734,187.467]}}} */
  results?: HazardResultsBase;
  /** @default {"buffer_distance":200,"time_horizon":2022,"climate_scenario":"Current","model_id":"ASCE_7_22_Wind","end_point":"point/wind","latitude":40.8879,"longitude":-72.7636} */
  request?: PointRequestModel;
  /**
   * Additional Info
   * A string containing any supplmental information that needs to be returned in the response
   */
  additional_info?: string | null;
  /** @default {"model_name":"ASCE 7-22 wind load standard","hazard_type":"WIND","sub_hazard_type":"INTEGRATED_WIND","supplier":"American Society of Civil Engineers (ASCE)","version":"ASCE722 - 10.71","supported_geography":{"country":"US"},"temporal_meta":{"time_horizons":{"2022":{"climate_scenarios":{"Current":{"return_periods":[10,25,50,100,300,700,1700,3000,10000]}}}}},"hazard_information":{"description":"Json dictionary containing the windspeeds across a range of mean-recurrence intervals (MRIs). Specifically, the 10, 25, 50, 100, 300, 700, 1700, 3000, 10000, 100000, and 1000000 year MRIs. The values come from the ASCE 7-22 ArcGIS rest API - https://gis.asce.org/arcgis/rest/services/ASCE722","intensity_measures":{"MAX_3S_GUST":{"im_description":"3-second sustained gust windspeed","unit":"mph"}}},"cost":{"area":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0},"expensable":false,"point":{"description":"This model has no charge associated with it.","max":0,"min":0,"price_per_unit":0}}} */
  metadata?: HazardSourceModel;
  /**
   * A list of custom units specified by the user
   * @example {"FLOOD_DEPTH":"cm"}
   */
  response_units?: UnitModel | null;
}

/** IntensityMeasureModel */
export interface IntensityMeasureModel {
  /**
   * Unit
   * Intensity measure unit
   * @example "cm/s"
   */
  unit: string;
  /**
   * Im Description
   * Text description of the intensity measure
   * @example "Peak ground velocity"
   */
  im_description: string;
}

/** IntensityMeasureType */
export enum IntensityMeasureType {
  FLOOD_DEPTH = "FLOOD_DEPTH",
  FLOOD_ZONES = "FLOOD_ZONES",
  FLAME_LENGTH = "FLAME_LENGTH",
  ADJACENT_FLAME_LENGTH = "ADJACENT_FLAME_LENGTH",
  EMBER_COUNTS = "EMBER_COUNTS",
  BURN = "BURN",
  FIRE_WEATHER_INDEX = "FIRE_WEATHER_INDEX",
  MAX3SGUST = "MAX_3S_GUST",
  PEAK_GROUND_ACCELERATION = "PEAK_GROUND_ACCELERATION",
  PEAK_GROUND_VELOCITY = "PEAK_GROUND_VELOCITY",
  SPECTRALACCELERATIONT0P01 = "SPECTRAL_ACCELERATION_T0P01",
  SPECTRALACCELERATIONT0P02 = "SPECTRAL_ACCELERATION_T0P02",
  SPECTRALACCELERATIONT0P03 = "SPECTRAL_ACCELERATION_T0P03",
  SPECTRALACCELERATIONT0P05 = "SPECTRAL_ACCELERATION_T0P05",
  SPECTRALACCELERATIONT0P075 = "SPECTRAL_ACCELERATION_T0P075",
  SPECTRALACCELERATIONT0P1 = "SPECTRAL_ACCELERATION_T0P1",
  SPECTRALACCELERATIONT0P15 = "SPECTRAL_ACCELERATION_T0P15",
  SPECTRALACCELERATIONT0P2 = "SPECTRAL_ACCELERATION_T0P2",
  SPECTRALACCELERATIONT0P25 = "SPECTRAL_ACCELERATION_T0P25",
  SPECTRALACCELERATIONT0P3 = "SPECTRAL_ACCELERATION_T0P3",
  SPECTRALACCELERATIONT0P4 = "SPECTRAL_ACCELERATION_T0P4",
  SPECTRALACCELERATIONT0P5 = "SPECTRAL_ACCELERATION_T0P5",
  SPECTRALACCELERATIONT0P75 = "SPECTRAL_ACCELERATION_T0P75",
  SPECTRALACCELERATIONT1P0 = "SPECTRAL_ACCELERATION_T1P0",
  SPECTRALACCELERATIONT1P5 = "SPECTRAL_ACCELERATION_T1P5",
  SPECTRALACCELERATIONT2P0 = "SPECTRAL_ACCELERATION_T2P0",
  SPECTRALACCELERATIONT3P0 = "SPECTRAL_ACCELERATION_T3P0",
  SPECTRALACCELERATIONT4P0 = "SPECTRAL_ACCELERATION_T4P0",
  SPECTRALACCELERATIONT5P0 = "SPECTRAL_ACCELERATION_T5P0",
  SPECTRALACCELERATIONT7P5 = "SPECTRAL_ACCELERATION_T7P5",
  SPECTRALACCELERATIONT10 = "SPECTRAL_ACCELERATION_T10",
  EXTREME_ANNUAL_DRY_BULB = "EXTREME_ANNUAL_DRY_BULB",
  EXTREME_ANNUAL_WET_BULB = "EXTREME_ANNUAL_WET_BULB",
  AVG_DAILY_HOTTEST_MONTH = "AVG_DAILY_HOTTEST_MONTH",
  HEATINDEX98PERCENTILE = "HEAT_INDEX_98PERCENTILE",
  HEAT_INDEX = "HEAT_INDEX",
  WIND_CHILL = "WIND_CHILL",
  GROUND_SNOW_LOAD = "GROUND_SNOW_LOAD",
  ICE_THICKNESS = "ICE_THICKNESS",
  WIND_SPEED = "WIND_SPEED",
  EXTREMEPRECIPITATION24HR = "EXTREME_PRECIPITATION_24HR",
  WATER_DEPLETION = "WATER_DEPLETION",
}

/** PointRequestModel */
export interface PointRequestModel {
  /**
   * Buffer Distance
   * The buffer distance arround the asset
   * @example 0.5
   */
  buffer_distance: number;
  /**
   * Time Horizon
   * Time horizon describing the
   * @example 2022
   */
  time_horizon: number;
  /**
   * Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century
   * @example "RCP_2P6"
   */
  climate_scenario: ClimateScenario;
  /**
   * Model Id
   * The id of the hazard model requested. Note that if left blank the preferred model will be used.
   * @example "FSF_Flood_v2"
   */
  model_id: string;
  /**
   * End Point
   * path to the endpoint employed
   */
  end_point: string;
  /**
   * Latitude
   * The latitude coordinate value of a geographical point (between -90 and 90).
   * @example "29.37141"
   */
  latitude: number;
  /**
   * Longitude
   * The longitude coordinate value of a geographical point (between -180 and 180).
   * @example "-95.03316"
   */
  longitude: number;
}

/** PreScreenHazardType */
export enum PreScreenHazardType {
  COASTAL_FLOODING = "COASTAL_FLOODING",
  LANDSLIDE = "LANDSLIDE",
  VOLCANO = "VOLCANO",
  EARTHQUAKE_SHAKING = "EARTHQUAKE_SHAKING",
  CYCLONIC_WIND = "CYCLONIC_WIND",
  TORNADO = "TORNADO",
  TSUNAMI = "TSUNAMI",
  WILDFIRE = "WILDFIRE",
  RAILWAY = "RAILWAY",
  NUCLEAR = "NUCLEAR",
  HIGHWAY = "HIGHWAY",
  AIRPORT = "AIRPORT",
  AVALANCHE = "AVALANCHE",
  INTEGRATED_FLOODING = "INTEGRATED_FLOODING",
  PIPELINE = "PIPELINE",
}

/** PrescreeningHazardModel */
export interface PrescreeningHazardModel {
  /** Hazard Rating */
  hazard_rating?: number | null;
  /** Description */
  description: string;
  /** Screening Criteria */
  screening_criteria: string;
  /** Status Code */
  status_code: number;
  screening_values: PrescreeningResultsModel;
  prescreening_hazard_type: PreScreenHazardType;
  [key: string]: any;
}

/** PrescreeningResponseModel */
export interface PrescreeningResponseModel {
  /** Hazards */
  hazards: Record<string, PrescreeningHazardModel>;
}

/** PrescreeningResultsModel */
export interface PrescreeningResultsModel {
  /** Values */
  values: Record<string, any>;
  /** Units */
  units: Record<string, UnitbaseModel>;
}

/** Region */
export enum Region {
  NotSuported = "Not_Suported",
  Africa = "Africa",
  Americas = "Americas",
  Asia = "Asia",
  Europe = "Europe",
  Oceania = "Oceania",
  Global = "Global",
}

/** SiteElevationRequest */
export interface SiteElevationRequest {
  /**
   * Geometry
   * GeoJSON definition of an asset geometry
   * @example {"geometry":{"coordinates":[-80.1943207,25.7683775],"type":"Point"},"properties":{"name":"Example Point","description":"This is an example point feature"},"type":"Feature"}
   */
  geometry: Record<string, any>;
  /**
   * Buffer Distance
   * The distance in km that a buffer geometry will be created, offset from the asset geometry.
   * @example 5.5
   */
  buffer_distance?: number | null;
}

/** SiteElevationResponseModel */
export interface SiteElevationResponseModel {
  geo_json: GeoJSONGeometry;
  /** Description */
  description: string;
  /** Values */
  values: Record<string, any>;
  download_urls?: CacheModel | null;
}

/** State */
export enum State {
  NotSuported = "Not_Suported",
  California = "California",
}

/** SubHazardType */
export enum SubHazardType {
  EARTHQUAKE_SHAKING = "EARTHQUAKE_SHAKING",
  INTEGRATED_FLOODING = "INTEGRATED_FLOODING",
  RIVERINE_FLOODING = "RIVERINE_FLOODING",
  COASTAL_FLOODING = "COASTAL_FLOODING",
  STORMWATER_FLOODING = "STORMWATER_FLOODING",
  GROUNDWATER_FLOODING = "GROUNDWATER_FLOODING",
  INTEGRATED_WIND = "INTEGRATED_WIND",
  CYCLONIC_WIND = "CYCLONIC_WIND",
  WILDFIRE = "WILDFIRE",
  TORNADO = "TORNADO",
  HEAT = "HEAT",
  COLD = "COLD",
  SNOW_PRESSURE = "SNOW_PRESSURE",
  ICE_THICKNESS = "ICE_THICKNESS",
  PRECIPITATION = "PRECIPITATION",
  DROUGHT = "DROUGHT",
}

/** TaskQueue */
export interface TaskQueue {
  /** Task Id */
  task_id: string;
  task_status: TaskStatus;
  /** Time Stamp */
  time_stamp: string;
  /** Message */
  message: string | null;
}

/** TaskStatus */
export enum TaskStatus {
  STARTED = "STARTED",
  RUNNING = "RUNNING",
  FAILED = "FAILED",
  NOTFOUND = "NOTFOUND",
  COMPLETE = "COMPLETE",
}

/** TemporalMetaModel */
export interface TemporalMetaModel {
  /** Time Horizons */
  time_horizons: Record<string, TimeHorizonModel>;
}

/** TimeHorizonModel */
export interface TimeHorizonModel {
  /** Climate Scenarios */
  climate_scenarios: Record<string, ClimateScenarioModel> | null;
}

/** UnitModel */
export interface UnitModel {
  /**
   * Units
   * A dictionary of intensity measures and their corresponding units
   * @example {"FLOOD_DEPTH":"cm"}
   */
  units: Record<string, UnitbaseModel>;
}

/** UnitbaseModel */
export interface UnitbaseModel {
  /**
   * Unit
   * Intensity measure unit
   * @example "cm/s"
   */
  unit: string;
}

/** ValidationError */
export interface ValidationError {
  /** Location */
  loc: (string | number)[];
  /** Message */
  msg: string;
  /** Error Type */
  type: string;
}

export type QueryParamsType = Record<string | number, any>;
export type ResponseFormat = keyof Omit<Body, "body" | "bodyUsed">;

export interface FullRequestParams extends Omit<RequestInit, "body"> {
  /** set parameter to `true` for call `securityWorker` for this request */
  secure?: boolean;
  /** request path */
  path: string;
  /** content type of request body */
  type?: ContentType;
  /** query params */
  query?: QueryParamsType;
  /** format of response (i.e. response.json() -> format: "json") */
  format?: ResponseFormat;
  /** request body */
  body?: unknown;
  /** base url */
  baseUrl?: string;
  /** request cancellation token */
  cancelToken?: CancelToken;
}

export type RequestParams = Omit<
  FullRequestParams,
  "body" | "method" | "query" | "path"
>;

export interface ApiConfig<SecurityDataType = unknown> {
  baseUrl?: string;
  baseApiParams?: Omit<RequestParams, "baseUrl" | "cancelToken" | "signal">;
  securityWorker?: (
    securityData: SecurityDataType | null
  ) => Promise<RequestParams | void> | RequestParams | void;
  customFetch?: typeof fetch;
}

export interface HttpResponse<D extends unknown, E extends unknown = unknown>
  extends Response {
  data: D;
  error: E;
}

type CancelToken = Symbol | string | number;

export enum ContentType {
  Json = "application/json",
  FormData = "multipart/form-data",
  UrlEncoded = "application/x-www-form-urlencoded",
  Text = "text/plain",
}

export class HttpClient<SecurityDataType = unknown> {
  public baseUrl: string = "";
  private securityData: SecurityDataType | null = null;
  private securityWorker?: ApiConfig<SecurityDataType>["securityWorker"];
  private abortControllers = new Map<CancelToken, AbortController>();
  private customFetch = (...fetchParams: Parameters<typeof fetch>) =>
    fetch(...fetchParams);

  private baseApiParams: RequestParams = {
    credentials: "same-origin",
    headers: {},
    redirect: "follow",
    referrerPolicy: "no-referrer",
  };

  constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
    Object.assign(this, apiConfig);
  }

  public setSecurityData = (data: SecurityDataType | null) => {
    this.securityData = data;
  };

  protected encodeQueryParam(key: string, value: any) {
    const encodedKey = encodeURIComponent(key);
    return `${encodedKey}=${encodeURIComponent(
      typeof value === "number" ? value : `${value}`
    )}`;
  }

  protected addQueryParam(query: QueryParamsType, key: string) {
    return this.encodeQueryParam(key, query[key]);
  }

  protected addArrayQueryParam(query: QueryParamsType, key: string) {
    const value = query[key];
    return value.map((v: any) => this.encodeQueryParam(key, v)).join("&");
  }

  protected toQueryString(rawQuery?: QueryParamsType): string {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter(
      (key) => "undefined" !== typeof query[key]
    );
    return keys
      .map((key) =>
        Array.isArray(query[key])
          ? this.addArrayQueryParam(query, key)
          : this.addQueryParam(query, key)
      )
      .join("&");
  }

  protected addQueryParams(rawQuery?: QueryParamsType): string {
    const queryString = this.toQueryString(rawQuery);
    return queryString ? `?${queryString}` : "";
  }

  private contentFormatters: Record<ContentType, (input: any) => any> = {
    [ContentType.Json]: (input: any) =>
      input !== null && (typeof input === "object" || typeof input === "string")
        ? JSON.stringify(input)
        : input,
    [ContentType.Text]: (input: any) =>
      input !== null && typeof input !== "string"
        ? JSON.stringify(input)
        : input,
    [ContentType.FormData]: (input: any) =>
      Object.keys(input || {}).reduce((formData, key) => {
        const property = input[key];
        formData.append(
          key,
          property instanceof Blob
            ? property
            : typeof property === "object" && property !== null
            ? JSON.stringify(property)
            : `${property}`
        );
        return formData;
      }, new FormData()),
    [ContentType.UrlEncoded]: (input: any) => this.toQueryString(input),
  };

  protected mergeRequestParams(
    params1: RequestParams,
    params2?: RequestParams
  ): RequestParams {
    return {
      ...this.baseApiParams,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(this.baseApiParams.headers || {}),
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {}),
      },
    };
  }

  protected createAbortSignal = (
    cancelToken: CancelToken
  ): AbortSignal | undefined => {
    if (this.abortControllers.has(cancelToken)) {
      const abortController = this.abortControllers.get(cancelToken);
      if (abortController) {
        return abortController.signal;
      }
      return void 0;
    }

    const abortController = new AbortController();
    this.abortControllers.set(cancelToken, abortController);
    return abortController.signal;
  };

  public abortRequest = (cancelToken: CancelToken) => {
    const abortController = this.abortControllers.get(cancelToken);

    if (abortController) {
      abortController.abort();
      this.abortControllers.delete(cancelToken);
    }
  };

  public request = async <T = any, E = any>({
    body,
    secure,
    path,
    type,
    query,
    format,
    baseUrl,
    cancelToken,
    ...params
  }: FullRequestParams): Promise<HttpResponse<T, E>> => {
    const secureParams =
      ((typeof secure === "boolean" ? secure : this.baseApiParams.secure) &&
        this.securityWorker &&
        (await this.securityWorker(this.securityData))) ||
      {};
    const requestParams = this.mergeRequestParams(params, secureParams);
    const queryString = query && this.toQueryString(query);
    const payloadFormatter = this.contentFormatters[type || ContentType.Json];
    const responseFormat = format || requestParams.format;

    return this.customFetch(
      `${baseUrl || this.baseUrl || ""}${path}${
        queryString ? `?${queryString}` : ""
      }`,
      {
        ...requestParams,
        headers: {
          ...(requestParams.headers || {}),
          ...(type && type !== ContentType.FormData
            ? { "Content-Type": type }
            : {}),
        },
        signal:
          (cancelToken
            ? this.createAbortSignal(cancelToken)
            : requestParams.signal) || null,
        body:
          typeof body === "undefined" || body === null
            ? null
            : payloadFormatter(body),
      }
    ).then(async (response) => {
      const r = response.clone() as HttpResponse<T, E>;
      r.data = null as unknown as T;
      r.error = null as unknown as E;

      const data = !responseFormat
        ? r
        : await response[responseFormat]()
            .then((data) => {
              if (r.ok) {
                r.data = data;
              } else {
                r.error = data;
              }
              return r;
            })
            .catch((e) => {
              r.error = e;
              return r;
            });

      if (cancelToken) {
        this.abortControllers.delete(cancelToken);
      }

      if (!response.ok) throw data;
      return data;
    });
  };
}

/**
 * @title HAPI - Hazard API
 * @version 0.1.0
 *
 * Welcome to **HAPI**!
 *
 *  - To access the endpoints you will need to enter your **Iris** bearer token after clicking on the ``Authorize`` button below.
 *  - HAPI supports queries in point and area (geojson) asset geometries. Each of the following endpoints provides an explanation of its corresponding input parameters.
 *  - To see a full list of all available hazard models and their information, go to the ``metadata/source`` and ``metadata/model`` endpoints below, respectively.
 *
 *
 * **<font color="red">Note</font>:** HAPI is available to all Arup discipline teams, but usage is subject to fees. In addition to provider costs for importing paid datasets, commercial and research projects incur usage fees to support hosting, compute, and maintenance expenses.     Before using the API, please contact Ibrahim Almufti, Hedi Smida, or Stevan Gavrilovic and send a brief note with details about your project—including project type, client name, job number, number and type of assets or infrastructure systems, geographic coverage, etc. This will help us ensure that the platform is set up to scale, meet your needs, and provide you with a quote.
 *
 * **Additional Arguments (additional_args)**
 *
 *  The additional arguments field allows users to supply additional instructions to HAPI. Some models may support advanced features which will only be activated
 *  when the corresponding additional arguments are given to HAPI. See model metadata for more information about the individual models and supported optional arguments. Some general additional arguments include:
 *  - ``no_cache``=HAPI has a cache system implemented. That is, it will save the response of every endpoint call and return it from the cache if the same call is made again, i.e.,     with the same input parameters. To run HAPi without pulling from the cache, include the text ``no_cache`` in the ``additional_args`` field.
 *  - ``units``=HAPI supports custom units. The default units from the hazard provider is given in the model metadata. To use custom units, you need to pass a     dictionary to the ``additional_args`` field in the following format: ``units={"IM_1":"unit_1", "IM_2":"unit_2"}`` where IM_n is an intensity measure and unit_n     is its corresponding unit tag, e.g., cm, cm/s, kilometer, and miles per hour. Both full unit names and abbreviations are accepted. Since some hazard sources provide more than one intensity measure,     the user should provide a list of intensity measures and their corresponding units. If units are not provided, HAPI will employ the default units from the hazard provider.     Note that the response units are provided in the response under the key ``response_units``
 */
export class Api<
  SecurityDataType extends unknown
> extends HttpClient<SecurityDataType> {
  ping = {
    /**
     * No description
     *
     * @tags ping
     * @name RequestPong
     * @summary Request Pong
     * @request GET:/ping/
     * @secure
     */
    requestPong: (params: RequestParams = {}) =>
      this.request<string, any>({
        path: `/ping/`,
        method: "GET",
        secure: true,
        format: "json",
        ...params,
      }),
  };
  point = {
    /**
     * @description Flood hazard API Current supported models are: - First Street Foundation v2.0 integrated flood model raster datasets hosted at Arup. Model id: **FSF_Flood_v2** - First Street Foundation v3.0 integrated flood model raster datasets hosted at Arup. Model id: **FSF_Flood_v3** - First Street Foundation v4.0 integrated flood model raster datasets hosted at Arup. Model id: **FSF_Flood_v4** - Fathom US pluvial flood dataset v2.0. Model id: **Fathom_US_v2_p** - Fathom US fluvial flood dataset v2.0. Model id: **Fathom_US_v2_f** - Fathom US pluvial flood dataset v3.1. Model id: **Fathom_US_v3_p** - Fathom US fluvial flood dataset v3.1. Model id: **Fathom_US_v3_f** - Fathom US coastal flood dataset v3.1. Model id: **Fathom_US_v3_c** - Fathom UK pluvial flood dataset v1.1.0. Model id: **Fathom_UK_v1_p** - Fathom UK fluvial flood dataset v1.1.0. Model id: **Fathom_UK_v1_f** - Fathom UK coastal flood dataset v1.1.0. Model id: **Fathom_UK_v1_c** - Fathom Global pluvial flood dataset v3.0. Model id: **Fathom_Global_v3_p** - Fathom Global fluvial flood dataset v3.0. Model id: **Fathom_Global_v3_f** - Fathom Global coastal flood dataset v3.0. Model id: **Fathom_Global_v3_c** - Defra UK Risk of Flooding from Surface Water (RoFSW) v8. Model id: **Defra_RoFSW_v8** For detailed information about a particular model, use the model metadata endpoint with the model id as input. \!/ Note that for the Fathom US and Global models, respectively, if you call the fluvial/pluvial models in succession you will only be charged once given that the location (lat./lon.), time horizon, climate scenario, and buffer distance are the same. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 25.7683775 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -80.1943207 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.5]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Fathom_US_v2_p, Fathom_US_v2_f, Fathom_Global_v3_f, Fathom_Global_v3_p, Fathom_Global_v3_c]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetFloodHazardAtPoint
     * @summary Get Flood Hazard At Point
     * @request POST:/point/flood
     * @secure
     */
    getFloodHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.1
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardFloodPointResponse, HTTPValidationError>({
        path: `/point/flood`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Seismic hazard API Current supported models are: - 2018 USGS National Seismic Hazard Model (NSHM). Model id: **USGS_NSHM_2018** - Global Earthquake Model (GEM) - Atlas API. Model id: **GEM_Seismic_v1** - Global Earthquake Model (GEM) - Public 475-year PGA Map. Model id: **GEM_Seismic_Public_475_v2021** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 37.7885495 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -122.3991892 - **Buffer Distance [Optional. Default=200]**: The distance in km, from the provided point, for which the faults will be provided. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [GEM_Seismic_v1]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetSeismicHazardAtPoint
     * @summary Get Seismic Hazard At Point
     * @request POST:/point/seismic
     * @secure
     */
    getSeismicHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /**
         * Buffer Distance
         * @default 500
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardSeismicPointResponse, HTTPValidationError>({
        path: `/point/seismic`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Wind hazard API Current supported models are: - ASCE 7-22 wind load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Wind** - Reask Global topical cyclone dataset v2.0. Model id: **Reask_v2** - Dlubal wind with R+R mapping to 3s gust hazard curve: **Dlubal_Wind_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Results summary: - **Windspeeds** - 3-second gust windspeed [mph] - **Elevation** - site elevation [ft] (only available when using ASCE_7_22_Wind) Notes: - For ASCE 7-22, only sites within the continental United States are currently supported. - For Dlubal, mapping is only available for sites that are geographically located within the area covered by the European EN 1991-1-4 (Eurocode 1 | wind actions) standard - Reask has global coverage Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 40.8879 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -72.7636 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2035. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Buffer Distance [Optional. Default=0.5]**: The distance in km, around the provided point, for which a clipped raster of the wind speeds will be returned. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Reask_v2]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetWindHazardAtPoint
     * @summary Get Wind Hazard At Point
     * @request POST:/point/wind
     * @secure
     */
    getWindHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 200
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardWindPointResponse, HTTPValidationError>({
        path: `/point/wind`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Wildfire hazard API Current supported models are: - First Street Foundation v2.0 Wildfire model raster datasets hosted at Arup. Model id: **FSF_Wildfire_v2** - GFDDR-CSIRO max fire weather index (FWI) map hosted at Arup. Model id: **GFDRR_CSIRO_Wildfire_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 38.705506 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -122.598881 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2053. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.2]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetFireHazardAtPoint
     * @summary Get Fire Hazard At Point
     * @request POST:/point/fire
     * @secure
     */
    getFireHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.2
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardFirePointResponse | TaskQueue, HTTPValidationError>({
        path: `/point/fire`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Heat hazard API Current supported models are: - ASHRAE CLIMATIC DESIGN CONDITIONS 2021. Model id: **ASHRAE_meteo_2021** - NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6) **NEXGDDP_CMIP6_HEAT_v1** - LOCA2 california (CA) downscaled climate projections (CMIP-6) **LOCA2_CA_HEAT_v1** - UKCP18 climate projections for the United Kingdom **UKCP18_5km_HEAT_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 37.74690 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -122.43155 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=200]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A].
     *
     * @tags point
     * @name GetHeatHazardAtPoint
     * @summary Get Heat Hazard At Point
     * @request POST:/point/heat
     * @secure
     */
    getHeatHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.5
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardHeatPointResponse | TaskQueue, HTTPValidationError>({
        path: `/point/heat`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Snow hazard API Current supported models are: - ASCE 7-22 snow load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Snow** - ASCE 7-16 snow load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_16_Snow** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 39.47179 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -118.77481 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=100]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Note: No paid datasets for snow hazard]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetSnowHazardAtPoint
     * @summary Get Snow Hazard At Point
     * @request POST:/point/snow
     * @secure
     */
    getSnowHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 300
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardSnowPointResponse, HTTPValidationError>({
        path: `/point/snow`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Ice hazard API Current supported models are: - ASCE 7-22 Ice load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Ice** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 37.205909 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -93.309871 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=100]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Note: No paid datasets for ice hazard]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetIceHazardAtPoint
     * @summary Get Ice Hazard At Point
     * @request POST:/point/ice
     * @secure
     */
    getIceHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 300
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardIcePointResponse, HTTPValidationError>({
        path: `/point/ice`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Tornado hazard API Current supported models are: - ASCE 7-22 Tornado Hazard Maps - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Tornado** - Note: to specify a custom footprint area, add this as an additional_arg: Ae={"area":15000,"units":"ft^2"} For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 37.493228 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -97.262292 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=100]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Note: No paid datasets for ice hazard]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetTornadoHazardAtPoint
     * @summary Get Tornado Hazard At Point
     * @request POST:/point/tornado
     * @secure
     */
    getTornadoHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 300
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardTornadoPointResponse, HTTPValidationError>({
        path: `/point/tornado`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Cold hazard API Current supported models are: - LOCA2 california (CA) downscaled climate projections (CMIP-6) **LOCA2_CA_COLD_v1** - NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6) **NEXGDDP_CMIP6_COLD_v1** - UKCP18 climate projections for the United Kingdom **UKCP18_5km_COLD_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 37.74690 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -122.43155 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=200]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the hazard dataset will be returned (if available). - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A].
     *
     * @tags point
     * @name GetColdHazardAtPoint
     * @summary Get Cold Hazard At Point
     * @request POST:/point/cold
     * @secure
     */
    getColdHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.5
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardColdPointResponse | TaskQueue, HTTPValidationError>({
        path: `/point/cold`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Precipitation hazard API Current supported models are: - NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6) **NEXGDDP_CMIP6_PRECIPITATION_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 49.2357 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -121.7624 - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=200]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A].
     *
     * @tags point
     * @name GetPrecipitationHazardAtPoint
     * @summary Get Precipitation Hazard At Point
     * @request POST:/point/precipitation
     * @secure
     */
    getPrecipitationHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.5
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<
        HazardPrecipitationPointResponse | TaskQueue,
        HTTPValidationError
      >({
        path: `/point/precipitation`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Drought hazard API Current supported models are: - Aqueduct 4.0 Water Risk Atlas - Baseline Water Depletion (BWD). Model id: **AQUEDUCT_DROUGHT_v4** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 34.388 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g. -118.561 - **Buffer Distance [Optional. Default=200]**: The distance in km, from the provided point, for which hazard maps will be provided (if available). - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags point
     * @name GetDroughtHazardAtPoint
     * @summary Get Drought Hazard At Point
     * @request POST:/point/drought
     * @secure
     */
    getDroughtHazardAtPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 200
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardDroughtPointResponse, HTTPValidationError>({
        path: `/point/drought`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),
  };
  area = {
    /**
     * @description Flood hazard API Current supported models are: - First Street Foundation v2.0 integrated flood model raster datasets hosted at Arup. Model id: **FSF_Flood_v2** - First Street Foundation v3.0 integrated flood model raster datasets hosted at Arup. Model id: **FSF_Flood_v3** - First Street Foundation v4.0 integrated flood model raster datasets hosted at Arup. Model id: **FSF_Flood_v4** - Fathom US pluvial flood dataset v2.0. Model id: **Fathom_US_v2_p** - Fathom US fluvial flood dataset v2.0. Model id: **Fathom_US_v2_f** - Fathom US pluvial flood dataset v3.1. Model id: **Fathom_US_v3_p** - Fathom US fluvial flood dataset v3.1. Model id: **Fathom_US_v3_f** - Fathom US coastal flood dataset v3.1. Model id: **Fathom_US_v3_c** - Fathom UK pluvial flood dataset v1.1.0. Model id: **Fathom_UK_v1_p** - Fathom UK fluvial flood dataset v1.1.0. Model id: **Fathom_UK_v1_f** - Fathom UK coastal flood dataset v1.1.0. Model id: **Fathom_UK_v1_c** - Fathom Global pluvial flood dataset v3.0. Model id: **Fathom_Global_v3_p** - Fathom Global fluvial flood dataset v3.0. Model id: **Fathom_Global_v3_f** - Fathom Global coastal flood dataset v3.0. Model id: **Fathom_Global_v3_c** - Defra UK Risk of Flooding from Surface Water (RoFSW) v8. Model id: **Defra_RoFSW_v8** For detailed information about a particular model, use the model metadata endpoint with the model id as input. \!/ Note that for the Fathom US and Global models, respectively, if you call the fluvial/pluvial models in succession you will only be charged once given that the location (geojson geometry), time horizon, climate scenario, and buffer distance are the same. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.5]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Fathom_US_v2_p, Fathom_US_v2_f, Fathom_Global_v3_f, Fathom_Global_v3_p, Fathom_Global_v3_c]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetFloodHazardOverArea
     * @summary Get Flood Hazard Over Area
     * @request POST:/area/flood
     * @secure
     */
    getFloodHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.1
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardFloodAreaResponse, HTTPValidationError>({
        path: `/area/flood`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Seismic hazard API Current supported models are: - 2018 USGS National Seismic Hazard Model (NSHM). Model id: **USGS_NSHM_2018** - Global Earthquake Model (GEM) - Atlas API. Model id: **GEM_Seismic_v1** - Global Earthquake Model (GEM) - Public 475-year PGA Map. Model id: **GEM_Seismic_Public_475_v2021** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Buffer Distance [Optional. Default=200]**: The distance in km, from the provided area, for which the faults will be provided. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [GEM_Seismic_v1]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetSeismicHazardOverArea
     * @summary Get Seismic Hazard Over Area
     * @request POST:/area/seismic
     * @secure
     */
    getSeismicHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /**
         * Buffer Distance
         * @default 500
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardSeismicAreaResponse, HTTPValidationError>({
        path: `/area/seismic`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Wind hazard API Current supported models are: - ASCE 7-22 wind load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Wind** - Reask Global topical cyclone dataset v2.0. Model id: **Reask_v2** - Dlubal wind with R+R mapping to 3s gust hazard curve: **Dlubal_Wind_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Results summary: - **Windspeeds** - 3-second gust windspeed [mph] - **Elevation** - site elevation [ft] (only available when using ASCE_7_22_Wind) Notes: - The windspeeds are provided at the centroid of the area geometry. - For ASCE 7-22, only sites within the continental United States are currently supported. - For Dlubal, mapping is only available for sites that are geographically located within the area covered by the European EN 1991-1-4 (Eurocode 1 | wind actions) standard - Reask has global coverage Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2035. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Buffer Distance [Optional. Default=0.5]**: The distance in km, around the provided area, for which a clipped raster of the wind speeds will be returned. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Reask_v2]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetWindHazardOverArea
     * @summary Get Wind Hazard Over Area
     * @request POST:/area/wind
     * @secure
     */
    getWindHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /** Model Id */
        model_id?: string | null;
        /**
         * Buffer Distance
         * @default 200
         */
        buffer_distance?: number | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardWindAreaResponse, HTTPValidationError>({
        path: `/area/wind`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Wildfire hazard API Current supported models are: - First Street Foundation v2.0 Wildfire model raster datasets hosted at Arup. Model id: **FSF_Wildfire_v2** - GFDDR-CSIRO max fire weather index (FWI) map hosted at Arup. Model id: **GFDRR_CSIRO_Wildfire_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2053. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.2]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetFireHazardOverArea
     * @summary Get Fire Hazard Over Area
     * @request POST:/area/fire
     * @secure
     */
    getFireHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.2
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardFireAreaResponse | TaskQueue, HTTPValidationError>({
        path: `/area/fire`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Heat hazard API Current supported models are: - ASHRAE CLIMATIC DESIGN CONDITIONS 2021. Model id: **ASHRAE_meteo_2021** - NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6) **NEXGDDP_CMIP6_HEAT_v1** - LOCA2 california (CA) downscaled climate projections (CMIP-6) **LOCA2_CA_HEAT_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.1]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A].
     *
     * @tags area
     * @name GetHeatHazardOverArea
     * @summary Get Heat Hazard Over Area
     * @request POST:/area/heat
     * @secure
     */
    getHeatHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.5
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardHeatAreaResponse | TaskQueue, HTTPValidationError>({
        path: `/area/heat`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Snow hazard API Current supported models are: - ASCE 7-22 snow load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Snow** - ASCE 7-16 snow load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_16_Snow** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=100]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Note: No paid datasets for snow hazard]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetSnowHazardAtArea
     * @summary Get Snow Hazard At Area
     * @request POST:/area/snow
     * @secure
     */
    getSnowHazardAtArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 300
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardSnowAreaResponse, HTTPValidationError>({
        path: `/area/snow`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Ice hazard API Current supported models are: - ASCE 7-22 ice load standard - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Ice** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=100]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Note: No paid datasets for ice hazard]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetIceHazardAtArea
     * @summary Get Ice Hazard At Area
     * @request POST:/area/ice
     * @secure
     */
    getIceHazardAtArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 300
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardIceAreaResponse, HTTPValidationError>({
        path: `/area/ice`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Tornado hazard API Current supported models are: - ASCE 7-22 Tornado Hazard Maps - American Society of Civil Engineers (ASCE). Model id: **ASCE_7_22_Tornado** - Note: to specify a custom footprint area, add this as an additional_arg: Ae={"area":15000,"units":"ft^2"} For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=100]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [Note: No paid datasets for ice hazard]. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetTornadoHazardAtArea
     * @summary Get Tornado Hazard At Area
     * @request POST:/area/tornado
     * @secure
     */
    getTornadoHazardAtArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 300
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardTornadoAreaResponse, HTTPValidationError>({
        path: `/area/tornado`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Precipitation hazard API Current supported models are: - NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6) **NEXGDDP_CMIP6_PRECIPITATION_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.1]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the heat dataset will be returned. - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A].
     *
     * @tags area
     * @name GetPrecipitationHazardOverArea
     * @summary Get Precipitation Hazard Over Area
     * @request POST:/area/precipitation
     * @secure
     */
    getPrecipitationHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.5
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardPrecipitationAreaResponse, HTTPValidationError>({
        path: `/area/precipitation`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Drought hazard API Current supported models are: - Aqueduct 4.0 Water Risk Atlas - Baseline Water Depletion (BWD). Model id: **AQUEDUCT_DROUGHT_v4** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2035. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Buffer Distance [Optional. Default=200]**: The distance in km, from the provided point, for which hazard maps will be provided (if available). - **Job Number [Optional]**: The project ID. Required if using a paid dataset. - **Additional Arguments [Optional]**: A comma separated string list of optional arguments. See above for more infomation.
     *
     * @tags area
     * @name GetDroughtHazardOverArea
     * @summary Get Drought Hazard Over Area
     * @request POST:/area/drought
     * @secure
     */
    getDroughtHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /** Model Id */
        model_id?: string | null;
        /**
         * Buffer Distance
         * @default 200
         */
        buffer_distance?: number | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardDroughtAreaResponse, HTTPValidationError>({
        path: `/area/drought`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Cold hazard API Current supported models are: - LOCA2 california (CA) downscaled climate projections (CMIP-6) **LOCA2_CA_COLD_v1** - NASA Earth Exchange Global Daily Downscaled Projections (NEX-GDDP-CMIP6) **NEXGDDP_CMIP6_COLD_v1** - UKCP18 climate projections for the United Kingdom **UKCP18_5km_COLD_v1** For detailed information about a particular model, use the model metadata endpoint with the model id as input. Input parameters: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry type should be a polygon or multi-polygon and should only describe one asset. - **Time Horizon [Optional. Default=None]**: Time horizon that incorporates the effect of climate change, e.g., 2052. If not provided, the most current time horizon of the hazard model will be used. - **Climate Scenario [Optional. Default=Current]**: Climate scenario that represents future levels of greenhouse gas concentrations throughout the 21st century, e.g., SSP2_4P5 - **Buffer Distance [Optional. Default=0.1]**: The distance in km, around the provided point, for which download links for a clipped raster and PNG of the cold dataset will be returned (if available). - **Model ID [Optional]**: The id of the hazard model requested. Note that if left blank the preferred model will be used. - **Job Number [Optional]**: The project ID. Required if using a paid dataset [N/A].
     *
     * @tags area
     * @name GetColdHazardOverArea
     * @summary Get Cold Hazard Over Area
     * @request POST:/area/cold
     * @secure
     */
    getColdHazardOverArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Time Horizon */
        time_horizon?: number | null;
        /** Climate Scenario */
        climate_scenario?: string | null;
        /**
         * Buffer Distance
         * @default 0.5
         */
        buffer_distance?: number | null;
        /** Model Id */
        model_id?: string | null;
        /** Job Number */
        job_number?: string | null;
        /** Additional Args */
        additional_args?: string | null;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardColdAreaResponse | TaskQueue, HTTPValidationError>({
        path: `/area/cold`,
        method: "POST",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),
  };
  metadata = {
    /**
     * No description
     *
     * @tags metadata
     * @name RequestHazardModels
     * @summary Request Hazard Models
     * @request GET:/metadata/source
     * @secure
     */
    requestHazardModels: (
      query: {
        hazard_type: HazardType;
      },
      params: RequestParams = {}
    ) =>
      this.request<AvailableHazardModels, HTTPValidationError>({
        path: `/metadata/source`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * No description
     *
     * @tags metadata
     * @name RequestSubHazardModels
     * @summary Request Sub Hazard Models
     * @request GET:/metadata/subhazard_source
     * @secure
     */
    requestSubHazardModels: (
      query: {
        sub_hazard_type: SubHazardType;
      },
      params: RequestParams = {}
    ) =>
      this.request<AvailableHazardModels, HTTPValidationError>({
        path: `/metadata/subhazard_source`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * No description
     *
     * @tags metadata
     * @name RequestModelMetadata
     * @summary Request Model Metadata
     * @request GET:/metadata/model
     * @secure
     */
    requestModelMetadata: (
      query: {
        /** Model Tag */
        model_tag: string;
      },
      params: RequestParams = {}
    ) =>
      this.request<HazardSourceModel, HTTPValidationError>({
        path: `/metadata/model`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),
  };
  pricing = {
    /**
     * No description
     *
     * @tags pricing
     * @name RequestHazardCost
     * @summary Request Hazard Cost
     * @request GET:/pricing/cost
     * @secure
     */
    requestHazardCost: (
      query: {
        /** Area */
        area: number;
        /** Unit */
        unit: string;
        /** Model Id */
        model_id: string;
      },
      params: RequestParams = {}
    ) =>
      this.request<CostResponseModel, HTTPValidationError>({
        path: `/pricing/cost`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * No description
     *
     * @tags pricing
     * @name RequestHazardCostPoint
     * @summary Request Hazard Cost Point
     * @request GET:/pricing/cost/point
     * @secure
     */
    requestHazardCostPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
        /** Buffer Distance */
        buffer_distance: number;
        /** Model Id */
        model_id: string;
      },
      params: RequestParams = {}
    ) =>
      this.request<CostResponseModel, HTTPValidationError>({
        path: `/pricing/cost/point`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * No description
     *
     * @tags pricing
     * @name RequestHazardCostArea
     * @summary Request Hazard Cost Area
     * @request GET:/pricing/cost/area
     * @secure
     */
    requestHazardCostArea: (
      query: {
        /** Geometry Geojson */
        geometry_geojson: string;
        /** Buffer Distance */
        buffer_distance: number;
        /** Model Id */
        model_id: string;
      },
      params: RequestParams = {}
    ) =>
      this.request<CostResponseModel, HTTPValidationError>({
        path: `/pricing/cost/area`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),
  };
  task = {
    /**
     * No description
     *
     * @tags task
     * @name GetTaskStatus
     * @summary Get Task Status
     * @request GET:/task/status
     * @secure
     */
    getTaskStatus: (
      query: {
        /** Task Id */
        task_id: string;
      },
      params: RequestParams = {}
    ) =>
      this.request<TaskQueue, HTTPValidationError>({
        path: `/task/status`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * No description
     *
     * @tags task
     * @name DeleteTask
     * @summary Delete Task
     * @request GET:/task/delete
     * @secure
     */
    deleteTask: (
      query: {
        /** Task Id */
        task_id: string;
      },
      params: RequestParams = {}
    ) =>
      this.request<any, HTTPValidationError>({
        path: `/task/delete`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),
  };
  prescreening = {
    /**
     * @description Hazard Prescreening API Prescreens the site against a list of hazards Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 25.7683775 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -80.1943207
     *
     * @tags prescreening
     * @name PrescreenPoint
     * @summary Prescreen Point
     * @request GET:/prescreening/point
     * @secure
     */
    prescreenPoint: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
      },
      params: RequestParams = {}
    ) =>
      this.request<PrescreeningResponseModel, HTTPValidationError>({
        path: `/prescreening/point`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),
  };
  geometry = {
    /**
     * @description Returns a GeoJSON string of a buffer geometry around an asset. If asset geometry is a point, a square buffer will be created around the point. Otherwise, buffer geometry will be offset from the asset geometry by the provided distance. Input parameters in the request body: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format as a string. Note, geometry should only represent one asset or feature. - **Buffer Distance**: The distance in km that a buffer geometry will be created, offset from the asset geometry.
     *
     * @tags geometry
     * @name RequestBufferGeometry
     * @summary Request Buffer Geometry
     * @request POST:/geometry/buffer_distance
     * @secure
     */
    requestBufferGeometry: (data: BufferRequest, params: RequestParams = {}) =>
      this.request<GeoJSONGeometry, HTTPValidationError>({
        path: `/geometry/buffer_distance`,
        method: "POST",
        body: data,
        secure: true,
        type: ContentType.Json,
        format: "json",
        ...params,
      }),
  };
  siteInformation = {
    /**
     * @description Returns the ASHRAE climate zone at a site along with a geojson of the surrounding climate zones within a 2000km circular buffer Input parameters: - **Latitude**: Geographic coordinate value of a point in degrees along the north-south direction (between -90 and 90), e.g., 25.7683775 - **Longitude**: Geographic coordinate value of a point in degrees along the east-west direction (between -180 and 180), e.g., -80.1943207
     *
     * @tags site_information
     * @name AshraeClimateZone
     * @summary Ashrae Climate Zone
     * @request GET:/site_information/climate_zone
     * @secure
     */
    ashraeClimateZone: (
      query: {
        /** Latitude */
        latitude: number;
        /** Longitude */
        longitude: number;
      },
      params: RequestParams = {}
    ) =>
      this.request<ASHRAEClimateZoneResponseModel, HTTPValidationError>({
        path: `/site_information/climate_zone`,
        method: "GET",
        query: query,
        secure: true,
        format: "json",
        ...params,
      }),

    /**
     * @description Returns a dictionary containing the elevation statistics for a site and the buffer area Input parameters in the request body: - **Geometry in GeoJSON format**: Asset geometry in GeoJSON format. Note that the geoJSON geometry should only describe one asset, i.e., have a single feature. - **Buffer Distance [Optional. Default=0.5]**: The distance in km, around the provided point, for which elevation statistics will be provided, along with a download link for the elevation raster. The default is used when the `buffer_distance` key is not included in the request body.
     *
     * @tags site_information
     * @name SiteElevationStats
     * @summary Site Elevation Stats
     * @request POST:/site_information/site_elevation
     * @secure
     */
    siteElevationStats: (
      data: SiteElevationRequest,
      params: RequestParams = {}
    ) =>
      this.request<SiteElevationResponseModel, HTTPValidationError>({
        path: `/site_information/site_elevation`,
        method: "POST",
        body: data,
        secure: true,
        type: ContentType.Json,
        format: "json",
        ...params,
      }),
  };
}
